import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import sharedStyles from "./Table.module.scss";
import classnames from "classnames";
import { NoResults } from "./components/NoResults";
import { observer } from "mobx-react-lite";
import { CrmComponentsErrorBox } from "../../CrmComponentsErrorBox";
import { TableRow } from "./components/Row";
import { Header } from "./components/Header";
import { MultipleItemsAction } from "@whatslly/ui-components/v2/Tables/Table/components/MultipleItemsAction/MultipleItemsAction";
import { Virtuoso } from "react-virtuoso";
import type { IEditColumnsStore } from "@whatslly/ui-components/EditColumns/types";
import { EditColumnsStoreSymbol } from "@whatslly/ui-components/EditColumns/types";
import { useOptionalDependencyInjector } from "@whatslly/commons/di";
import type { EditableTableProps, TableColumn } from "./TableTypes";
export const Table: <T extends Record<string, any> = any>(data: EditableTableProps<T>) => React.JSX.Element = observer(
  ({
    error,
    loading,
    items,
    sort,
    setSort,
    loadMoreIsLoading,
    searchTerm,
    emptyStateNode,
    searchEmptyStateNode,
    loadMore,
    hasMoreResults,
    getLink,
    linkTarget,
    columns,
    overrideGridTemplateColumns,
    className,
    headerClassName,
    rowOnClick,
    hoverActions,
    getActionsMenuActions,
    rowClassName,
    disableHoverActionsWrapperBackgroundColor,
    numberOfLoadingRows,
    enableVirtualScrolling: enableVirtualScrolling,
    selectedItemsOptions,
    storageKey,
    showSelectAllCheckbox,
    style,
    renderExpandedRow,
    isRowExpandable,
  }) => {
    const observer = useRef<IntersectionObserver | null>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const [expandedRows, setExpandedRows] = useState(new Set<string>());
    const toggleExpanded = useCallback((id: string) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    }, []);
    const columnsStore = useOptionalDependencyInjector<IEditColumnsStore>(EditColumnsStoreSymbol);

    useEffect(() => {
      if (storageKey && columnsStore) {
        columnsStore.initialize(storageKey, columns);
      }
    }, [columnsStore, columns, storageKey]);

    const visibleColumns = useMemo(() => {
      if (storageKey && columnsStore) {
        return columnsStore.getSortedColumns();
      }
      return columns;
    }, [columnsStore, columns, storageKey, columnsStore?.selectedColumns]);

    const headerColumns = useMemo(() => {
      return visibleColumns.map((col: TableColumn<any>) => ({
        title: col.title,
        centerHeader: col.centerHeader,
        keyInsideItem: col.keyInsideItem,
        disableSortBy: col.disableSortBy,
      }));
    }, [visibleColumns]);

    const onSort = useCallback(
      (keyInsideItem: string) => {
        if (!sort || !setSort) {
          return;
        }
        if (sort.keyInsideItem === keyInsideItem) {
          return setSort({ ...sort, direction: sort.direction === "asc" ? "desc" : "asc" });
        }
        setSort({ keyInsideItem, direction: "asc" });
      },
      [sort, setSort]
    );

    const tableIsOverflown = useMemo(() => {
      const table = tableRef.current;
      if (!table) {
        return false;
      }
      return table.scrollHeight >= table.clientHeight;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, loading]);

    const gridTemplateColumns = useMemo(() => {
      if (overrideGridTemplateColumns) {
        return overrideGridTemplateColumns;
      }
      return visibleColumns.reduce((acc: string, column: TableColumn<any>) => {
        acc += column.width ? `${column.width} ` : `1fr `;
        return acc;
      }, "");
    }, [visibleColumns, overrideGridTemplateColumns]);

    const allItemsSelected = useMemo(() => {
      return items?.length > 0 && selectedItemsOptions?.selectedItems?.length === items?.length;
    }, [items?.length, selectedItemsOptions?.selectedItems?.length]);

    const handleSelectAllItems = useCallback(() => {
      if (!selectedItemsOptions) return;

      if (allItemsSelected) {
        selectedItemsOptions.onDeselectAllItems();
      } else {
        items.forEach((item) => {
          if (!selectedItemsOptions.selectedItems.includes(item)) {
            selectedItemsOptions.onToggleRow(item);
          }
        });
      }
    }, [allItemsSelected, items, selectedItemsOptions]);

    const lastUserElementRef = useCallback(
      (node: HTMLAnchorElement) => {
        if (loading) {
          return;
        }
        if (observer.current) {
          observer.current.disconnect();
        }
        observer.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasMoreResults && !loadMoreIsLoading) {
            loadMore?.();
            observer.current?.disconnect();
          }
        });
        if (node) {
          observer.current.observe(node);
        }
      },
      [hasMoreResults, loadMore, loading]
    );

    useEffect(() => () => observer.current?.disconnect(), []);

    const Row = useCallback(
      (index: number) => {
        const row = items[index];
        if (!row) return <TableRow item={{}} columns={visibleColumns} loading={true} gridTemplateColumns={gridTemplateColumns} />;
        const expandable = renderExpandedRow ? (isRowExpandable ? isRowExpandable(row) : true) : false;
        const rowId = String(row._id ?? row.id ?? index);
        return (
          <TableRow
            onClick={(e, item) => rowOnClick?.(e, item)}
            getLink={(item) => getLink?.(item)}
            linkTarget={linkTarget}
            item={row}
            columns={visibleColumns}
            ref={index + 1 === items?.length ? lastUserElementRef : undefined}
            key={index}
            gridTemplateColumns={gridTemplateColumns}
            hoverActions={hoverActions}
            actionsMenuActions={getActionsMenuActions?.(row)}
            rowClassName={rowClassName?.(row)}
            disableHoverActionsWrapperBackgroundColor={disableHoverActionsWrapperBackgroundColor}
            onToggleRow={selectedItemsOptions?.onToggleRow}
            isSelected={selectedItemsOptions?.selectedItems?.includes(row)}
            isExpandable={expandable}
            isExpanded={expandedRows.has(rowId)}
            onToggleExpand={() => toggleExpanded(rowId)}
          />
        );
      },
      [
        items,
        visibleColumns,
        gridTemplateColumns,
        linkTarget,
        lastUserElementRef,
        hoverActions,
        getActionsMenuActions,
        rowClassName,
        disableHoverActionsWrapperBackgroundColor,
        selectedItemsOptions?.onToggleRow,
        selectedItemsOptions?.selectedItems,
        rowOnClick,
        getLink,
        renderExpandedRow,
        isRowExpandable,
        expandedRows,
        toggleExpanded,
      ]
    );

    return (
      <div className={classnames(sharedStyles.table, className)} style={style}>
        <div className={sharedStyles.tableHeader}>
          <Header
            sort={sort}
            onSort={onSort}
            loading={loading}
            columns={headerColumns}
            className={classnames(headerClassName, sharedStyles.stickyHeaderBackground)}
            gridTemplateColumns={gridTemplateColumns}
            tableIsOverflown={tableIsOverflown}
            showSelectedItems={Boolean(selectedItemsOptions)}
            onSelectAllItems={handleSelectAllItems}
            allItemsSelected={allItemsSelected}
            showSelectAllCheckbox={showSelectAllCheckbox && Boolean(items?.length)}
          />
        </div>
        {
          <>
            <div className={classnames(sharedStyles.tableDataWrapper)} ref={tableRef}>
              {error ? (
                <CrmComponentsErrorBox testId={`table-error`} error={error} />
              ) : loading ? (
                <>
                  {Array(numberOfLoadingRows ?? 10)
                    .fill(0)
                    .map((_, i) => (
                      <TableRow item={{}} columns={visibleColumns} loading={true} key={i} gridTemplateColumns={gridTemplateColumns} />
                    ))}
                </>
              ) : items?.length === 0 ? (
                <NoResults
                  isNoResults={!searchTerm}
                  isNoSearchResults={Boolean(searchTerm)}
                  noResults={emptyStateNode}
                  noSearchResults={searchEmptyStateNode}
                />
              ) : enableVirtualScrolling ? (
                <Virtuoso
                  totalCount={items.length}
                  data={items}
                  itemContent={(index) => {
                    if (loadMoreIsLoading && index === items.length - 1) {
                      return (
                        <>
                          {Row(index)}
                          <TableRow item={{}} columns={visibleColumns} loading={true} gridTemplateColumns={gridTemplateColumns} />
                        </>
                      );
                    }
                    return Row(index);
                  }}
                  increaseViewportBy={1000}
                  style={{ height: "100%" }}
                />
              ) : (
                <>
                  {items?.map((item, index) => {
                    const rowId = String(item._id ?? item.id ?? index);
                    return (
                      <React.Fragment key={index}>
                        {Row(index)}
                        {renderExpandedRow && expandedRows.has(rowId) && (
                          <div className={sharedStyles.expandedRow}>{renderExpandedRow(item)}</div>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {loadMoreIsLoading && <TableRow item={{}} columns={visibleColumns} loading={true} gridTemplateColumns={gridTemplateColumns} />}
                </>
              )}
            </div>
            {selectedItemsOptions && selectedItemsOptions.selectedItems.length > 0 && (
              <MultipleItemsAction selectedItemsOptions={selectedItemsOptions} />
            )}
          </>
        }
      </div>
    );
  }
);
